import {
  BellRing,
  BookOpenCheck,
  Boxes,
  Brain,
  ClipboardList,
  ClipboardPlus,
  GitCompareArrows,
  GraduationCap,
  PackageCheck,
  PlugZap,
  Radio,
  Timer,
  TrendingUp,
  Users,
} from "lucide-react";
import { Section, SectionHeader, CapabilityCard } from "@/components/ds";

const stages = [
  {
    icon: PackageCheck,
    title: "Prepare",
    body: "Cart, supply, and protocol readiness so every event starts from a known state.",
  },
  {
    icon: BellRing,
    title: "Activate",
    body: "Activation opens a shared event timeline the whole team can see immediately.",
  },
  {
    icon: Users,
    title: "Coordinate",
    body: "Role clarity, task sequencing, and synchronized clinical timers in the room.",
  },
  {
    icon: ClipboardPlus,
    title: "Document",
    body: "Structured capture during the event instead of reconstruction afterward.",
  },
  {
    icon: GitCompareArrows,
    title: "Review",
    body: "A consistent event record for debrief, quality review, and safety analysis.",
  },
  {
    icon: TrendingUp,
    title: "Improve",
    body: "Insight routed back into protocols, training, and readiness practices.",
  },
];

/** Resuscitation lifecycle — six connected stages as a horizontal journey. */
export const ResuscitationLifecycle = () => (
  <Section aria-labelledby="lifecycle-heading">
    <SectionHeader
      eyebrow="The resuscitation lifecycle"
      title={<span id="lifecycle-heading">One continuous loop, from readiness to improvement</span>}
      description="The platform is designed around the full arc of an event — not just the minutes in the room."
      className="reveal"
    />

    <ol className="reveal mt-12 grid gap-5 sm:grid-cols-2 lg:grid-cols-6">
      {stages.map(({ icon: Icon, title, body }, i) => (
        <li key={title} className="relative">
          <div className="ds-card flex h-full flex-col gap-3">
            <div className="flex items-center gap-3">
              <span className="inline-flex size-9 items-center justify-center rounded-full border border-primary/30 bg-primary/10 text-primary">
                <Icon className="size-4" />
              </span>
              <span className="font-mono text-xs tracking-[0.2em] text-muted-foreground">
                {String(i + 1).padStart(2, "0")}
              </span>
            </div>
            <h3 className="ds-h5">{title}</h3>
            <p className="ds-caption">{body}</p>
          </div>
          {i < stages.length - 1 ? (
            <span
              aria-hidden
              className="absolute -right-3 top-1/2 hidden h-px w-6 bg-border lg:block"
            />
          ) : null}
        </li>
      ))}
    </ol>
  </Section>
);

const capabilities = [
  {
    icon: Brain,
    title: "Real-time decision support",
    body: "Next-step, timing, and dosing prompts drawn from institutional protocols and expert practice.",
    status: "In development",
  },
  {
    icon: Timer,
    title: "Coordinated clinical timers",
    body: "One synchronized clock for compressions, rhythm checks, and medication intervals.",
    status: "In development",
  },
  {
    icon: Users,
    title: "Role and workflow guidance",
    body: "Clear task ownership so team members know their responsibility at a glance.",
    status: "In development",
  },
  {
    icon: ClipboardList,
    title: "Intelligent event documentation",
    body: "Structured capture during the event, with timestamps recorded as actions occur.",
    status: "In development",
  },
  {
    icon: PlugZap,
    title: "Device and data connectivity",
    body: "Integration pathways for monitors, defibrillators, and hospital record systems.",
    status: "Planned",
  },
  {
    icon: Boxes,
    title: "Inventory readiness",
    body: "Visibility into cart contents, expirations, and restock status before an event.",
    status: "In development",
  },
  {
    icon: BookOpenCheck,
    title: "Post-event review",
    body: "A consistent event record for debrief, quality review, and trend analysis.",
    status: "Planned",
  },
  {
    icon: GraduationCap,
    title: "Training and simulation",
    body: "Reuse the same workflow for drills, onboarding, and competency practice.",
    status: "Planned",
  },
];

/** Core capabilities — honest status chips on every card. */
export const CoreCapabilities = () => (
  <Section tone="mist" aria-labelledby="capabilities-heading">
    <SectionHeader
      eyebrow="Core capabilities"
      title={<span id="capabilities-heading">What the platform is designed to do</span>}
      description="Each capability is labeled with its current development status. Nothing here is described as cleared for clinical use."
      className="reveal"
    />
    <div className="reveal mt-10 grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
      {capabilities.map(({ icon, title, body, status }) => (
        <CapabilityCard
          key={title}
          icon={icon}
          title={title}
          status={status}
          statusTone={status === "In development" ? "info" : "neutral"}
        >
          {body}
        </CapabilityCard>
      ))}
    </div>
    <p className="ds-legal reveal mt-6">
      <Radio className="mr-1 inline size-3.5 align-[-2px]" aria-hidden />
      Capability descriptions reflect design intent during the pre-market development and
      validation phase.
    </p>
  </Section>
);
